import { createFileRoute, redirect } from '@tanstack/react-router';

interface ReviewPlatformsSearch {
  /** OAuth-callback signal — `select-business` or `error`. */
  status?: 'select-business' | 'error';
  /** Provider slug echoed back by the operations callback. */
  provider?: 'google' | 'facebook';
  /** ID of the OAuthToken row created by the operations callback. */
  oauthTokenId?: string;
  /** Optional URL-encoded reason if `status=error`. */
  reason?: string;
  /** Multi-location tenants only. */
  locationId?: string;
}

/**
 * NOT-576 — Review Platforms is now the `platforms` tab on Settings > Reviews.
 *
 * **This route must not be deleted.** Three callers outside the SPA still name
 * this address and cannot be changed in step with a frontend deploy:
 *
 * - `oauth.controller.ts` builds its `settingsPath` from it, so an OAuth round
 *   trip already in flight when a deploy lands comes back here;
 * - four `actionUrl` entries in `notification-event-catalog.ts` — the sync-health
 *   alerts — point here, and the ones already delivered keep pointing here
 *   forever;
 * - contractors' own bookmarks.
 *
 * Deleting it would 404 an owner following the alert that says their Google sync
 * broke. Keeping the backend on this address and forwarding here is deliberate:
 * operations and the SPA deploy independently, so pointing the callback at the
 * new path first would 404 for the window in between, and this redirect costs
 * one client-side hop.
 *
 * `validateSearch` is kept so the OAuth params survive into `beforeLoad` and
 * ride along to the tab that finishes the connect flow.
 */
export const Route = createFileRoute('/_dashboard/dashboard/settings/review-platforms')({
  validateSearch: (raw: Record<string, unknown>): ReviewPlatformsSearch => {
    const status =
      raw.status === 'select-business' || raw.status === 'error' ? raw.status : undefined;
    const provider =
      raw.provider === 'google' || raw.provider === 'facebook' ? raw.provider : undefined;
    return {
      status,
      provider,
      oauthTokenId: raw.oauthTokenId ? String(raw.oauthTokenId) : undefined,
      reason: raw.reason ? String(raw.reason) : undefined,
      locationId: raw.locationId ? String(raw.locationId) : undefined,
    };
  },
  beforeLoad: ({ search }) => {
    throw redirect({
      to: '/dashboard/settings/reviews',
      search: { ...search, tab: 'platforms' },
      // `replace`, so the browser's Back button leaves the screen rather than
      // returning to an address that only bounces forward again — and so a
      // spent OAuth callback URL does not sit in the history stack.
      replace: true,
    });
  },
});
